/**
 * date-utils.iso — le pont `Date` ↔ `'YYYY-MM-DD'` LOCAL du `DatePicker`.
 *
 * Sorti de `date-utils.ts` (règle des 300 lignes), qui le ré-exporte à l'identique :
 * les appelants importent toujours `@/lib/date-utils`.
 */

/**
 * `Date` → `'YYYY-MM-DD'` **local**, et son inverse.
 *
 * C'est le pont entre le `DatePicker` du design system, qui travaille en `Date`,
 * et les champs calendaires de l'API, qui sont des chaînes `YYYY-MM-DD` sans
 * fuseau (échéances, dates de contrôle, bornes de validité).
 *
 * ⚠️ **Le piège est le fuseau, et il ne se voit pas depuis Paris.**
 * `new Date('2026-06-12')` est interprété en **UTC minuit** : dans tout fuseau
 * négatif, `getDate()` rend le **11**. Une VGP « valide jusqu'au 12/06 inclus »
 * s'affiche alors comme expirant le 11 — un jour de moins sur un engin
 * réglementé. Et `toISOString().slice(0, 10)` fait la faute symétrique : depuis
 * Paris en été, une date choisie le 12 à 00 h 00 locale repart en `2026-06-11`.
 *
 * Ces deux fonctions étaient recopiées dans quatre fichiers — deux dialogues
 * qualité, un onglet de non-conformité, un module de planning. Quatre copies
 * d'un correctif de fuseau, c'est trois occasions de n'en corriger qu'une.
 */
export function toISODate(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

/** `'YYYY-MM-DD'` → `Date` **locale**. Rend `null` sur une chaîne vide ou
 *  inexploitable — un `Invalid Date` remonterait jusqu'au calendrier, qui
 *  afficherait une grille vide sans rien dire. */
export function fromISODate(iso: string | null | undefined): Date | null {
    if (!iso) return null
    const [y, m, d] = iso.split('-').map(Number)
    if (!y || !m || !d) return null
    const date = new Date(y, m - 1, d)
    return Number.isNaN(date.getTime()) ? null : date
}
